/**
 * Component Registry for Price Brothers Theme
 * 
 * Extends the base vantage theme registry with Price Brothers specific overrides.
 */

import type { ComponentRegistry } from '@vibesites/runtime';
import { registry as baseRegistry } from '@vibesites/theme-vantage/registry';
import { AccessibilityStatement } from './components/AccessibilityStatement';
import { AmenitiesList } from './components/AmenitiesList';
import { ApplyNow } from './components/ApplyNow';
import { ContactForm } from './components/ContactForm';
import { FAQList } from './components/FAQList';
import { FeaturedFloorPlan } from './components/FeaturedFloorPlan';
import { FloorplanShowcase } from './components/FloorplanShowcase';
import { FloorplansList } from './components/FloorplansList';
import { Footer } from './components/Footer';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { HeroSolidBG } from './components/HeroSolidBG';
import { HyperlocalHero } from './components/HyperlocalHero';
import { HyperlocalPageCopy } from './components/HyperlocalPageCopy';
import { NeighborhoodMap } from './components/NeighborhoodMap';
import { NeighborhoodSection } from './components/NeighborhoodSection';
import { PageBlurb } from './components/PageBlurb';
import { PageHero } from './components/PageHero';
import { PetPolicy } from './components/PetPolicy';
import { PhotoGallery } from './components/PhotoGallery';
import { PreferredEmployerBenefits } from './components/PreferredEmployerBenefits';
import { PreferredEmployerCTA } from './components/PreferredEmployerCTA';
import { PreferredEmployerList } from './components/PreferredEmployerList';
import { PropertyOverview } from './components/PropertyOverview';
import { ResidentAccess } from './components/ResidentAccess';
import { ScheduleTourForm } from './components/ScheduleTourForm';
import { SisterProperties } from './components/SisterProperties';
import { SitemapContent } from './components/SitemapContent';
import { SpecialOfferBanner } from './components/SpecialOfferBanner';
import { ThankYouContent } from './components/ThankYouContent';
import { VirtualTourEmbed } from './components/VirtualTourEmbed';

/**
 * Price Brothers specific component overrides.
 * These components have been customized for the Price Brothers brand.
 */
const pricebrothersOverrides: Partial<ComponentRegistry> = {
  AccessibilityStatement,
  AmenitiesList,
  ApplyNow,
  ContactForm,
  FAQList,
  FeaturedFloorPlan,
  FloorplanShowcase,
  FloorplansList,
  Footer,
  Header,
  Hero,
  HeroSolidBG,
  HyperlocalHero,
  HyperlocalPageCopy,
  NeighborhoodMap,
  NeighborhoodSection,
  PageBlurb,
  PageHero,
  PetPolicy,
  PhotoGallery,
  PreferredEmployerBenefits,
  PreferredEmployerCTA,
  PreferredEmployerList,
  PropertyOverview,
  ResidentAccess,
  ScheduleTourForm,
  SisterProperties,
  SitemapContent,
  SpecialOfferBanner,
  ThankYouContent,
  VirtualTourEmbed,
};

/**
 * Final merged registry for Price Brothers properties.
 * Inherits all components from the vantage theme, with Price Brothers overrides.
 */
export const registry: ComponentRegistry = {
  ...baseRegistry,
  ...pricebrothersOverrides,
};

export default registry;
